/**
 * AI Memory service layer - what the agent has memorized about the
 * company's drivers and vehicles (RGPD: managers can audit and purge it).
 *
 * All calls go through cdnktapi (authenticated axios instance); company
 * scoping is resolved server-side from the session token, and the agent
 * enforces it again in every SQL query.
 */
import axios from "./axios";

export type MemoryEntityType = "driver" | "vehicle" | "manager";

export interface MemoryFact {
  memory_id: number;
  entity_type: MemoryEntityType;
  entity_name: string | null;
  fact: string;
  importance_score: number;
  source: "auto" | "manual";
  last_confirmed_at: string | null;
}

export interface MemoryList {
  memories: MemoryFact[];
  count: number;
  total: number;
}

export const listMemories = async (limit = 50): Promise<MemoryList> => {
  const { data } = await axios.get<MemoryList>("/ai-agent/memory", {
    params: { limit },
  });
  return { memories: data.memories ?? [], count: data.count ?? 0, total: data.total ?? 0 };
};

export const getEntityMemory = async (
  entityName: string
): Promise<{ entity: string; context: string; has_memory: boolean }> => {
  const { data } = await axios.get(
    `/ai-agent/memory/entity/${encodeURIComponent(entityName)}`
  );
  return data;
};

export const saveMemoryNote = async (
  entity: string,
  fact: string
): Promise<void> => {
  await axios.post("/ai-agent/memory/note", { entity, fact });
};

export const deleteMemory = async (memoryId: number): Promise<void> => {
  await axios.delete(`/ai-agent/memory/${memoryId}`);
};
